import { useState } from "react";
import { Linkedin } from "lucide-react";
import { useContent, useSettings, fileUrl } from "@/lib/api";
import { Lightbox, PageHero, Reveal, SectionHead } from "@/components/site/common";
import { Stats } from "@/pages/Home";
import { useSeo } from "@/lib/seo";

function Timeline({ items, y }) {
  return (
    <div data-testid="legacy-timeline" className="relative">
      <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[var(--stone)]" />
      {items.map((m, i) => {
        const left = i % 2 === 0;
        const marker = i > 0 && items[i - 1].era !== m.era;
        return (
          <div key={m.id}>
            {marker && (
              <div data-testid="era-marker" className="relative my-10 flex md:justify-center pl-12 md:pl-0">
                <span className="bg-[var(--brass)] text-[var(--paper)] text-xs tracking-[0.18em] uppercase px-4 py-2">{y} · Tijiya Engineering becomes Tijiya Exports</span>
              </div>
            )}
            <Reveal className={`relative md:grid md:grid-cols-2 md:gap-16 mb-10 pl-12 md:pl-0`}>
              <span className={`absolute left-4 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rounded-full border-2 ${m.era === "exports" ? "bg-[var(--brass)] border-[var(--brass)]" : "bg-[var(--ivory)] border-[var(--steel)]"}`} />
              <div className={left ? "md:text-right md:pr-4" : "md:col-start-2 md:pl-4"}>
                <p data-testid={`milestone-year-${i}`} className="serif text-4xl text-[var(--brass)]">{m.year}</p>
                <h3 className="text-2xl mt-1">{m.title}</h3>
                <p className="text-sm text-[var(--graphite)] mt-2">{m.text}</p>
                {m.image && <img src={fileUrl(m.image)} alt="" className="mt-4 w-full max-w-sm object-cover border border-[var(--stone)] inline-block" loading="lazy" />}
              </div>
            </Reveal>
          </div>
        );
      })}
    </div>
  );
}

function Chapter({ num, title, years, img, sepia, children }) {
  return (
    <section className="wrap py-20 grid lg:grid-cols-12 gap-12 items-center border-b border-[var(--stone)]">
      <Reveal className={`lg:col-span-6 ${num === "II" ? "lg:order-2" : ""}`}><div className="img-frame aspect-[4/3] border border-[var(--stone)]"><img src={img} alt="" loading="lazy" className={`w-full h-full object-cover ${sepia ? "sepia" : ""}`} /></div></Reveal>
      <Reveal delay={120} className="lg:col-span-6">
        <p className="eyebrow mb-4">Chapter {num} · {years}</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl">{title}</h2>
        <div className="mt-6 space-y-4 text-[var(--graphite)] md:text-lg">{children}</div>
      </Reveal>
    </section>
  );
}

export function Legacy() {
  const { data: ms = [] } = useContent("milestones");
  const { data: s } = useSettings();
  const y = s?.exports_year || 2013;
  useSeo("Our Legacy", "Founded in 1987 as Tijiya Engineering; since 2013 Tijiya Exports. Forty years of steel fencing and construction hardware from Kolkata.");
  return (
    <div data-testid="legacy-page">
      <PageHero eyebrow="Our legacy · 1987 – Today" title="40 Years. Two Names. One Commitment to Quality." sub={`A family-owned Kolkata manufacturing house founded in 1987 as Tijiya Engineering. Since ${y} it has operated as Tijiya Exports, building on the same people, plant and standards.`} />
      <Chapter num="I" title="Tijiya Engineering" years={`1987 – ${y}`} img="/img/mill.jpg" sepia>
        <p>The foundation years. Tijiya Engineering established mild-steel manufacturing in Kolkata, built its galvanising and threading capability, and shipped its first export orders of fence fittings.</p>
        <p>Over this period the company was recognised by the Government of India as an Export House.</p>
      </Chapter>
      <section className="wrap py-20 text-center border-b border-[var(--stone)]">
        <Reveal className="max-w-3xl mx-auto">
          <p data-testid="bridge-quote" className="serif text-3xl md:text-4xl italic leading-snug">“The name on the gate changed. The people at the presses, and the standard we hold them to, did not.”</p>
          <p className="mt-6 eyebrow">[Founder / Managing Director name]</p>
        </Reveal>
      </section>
      <Chapter num="II" title="Tijiya Exports" years={`${y} – Today`} img="/img/containers.jpg">
        <p>The export era. A focused product range for international buyers in the USA and beyond, and an expanded catalogue of construction stakes, anchor bolts and threaded rods.</p>
        <p>The plant, galvanising lines and quality checks of Tijiya Engineering carry straight through.</p>
      </Chapter>
      <section className="wrap py-24">
        <SectionHead num="01" eyebrow="Timeline" title="Milestones across both eras" />
        <Timeline items={ms} y={y} />
      </section>
      <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-24">
        <div className="wrap grid md:grid-cols-3 gap-12">
          {[["Mission", "To make steel hardware that fits the first time, every time, and to deliver it on the date we promise."], ["Vision", "To be the manufacturer export buyers return to for a generation — known for consistency, not noise."], ["Values", "Precision in every dimension. Honesty in every quotation. Continuity as a family business."]].map(([t, d], i) => (
            <Reveal key={t} delay={i * 100} className="border-t border-[var(--brass)] pt-6"><h3 className="text-3xl">{t}</h3><p className="mt-4 text-[var(--graphite)]">{d}</p></Reveal>
          ))}
        </div>
      </section>
      <section className="wrap py-24">
        <SectionHead num="02" eyebrow="Infrastructure" title="The facility" sub="Placeholder imagery — to be replaced with photographs of the Tijiya plant." />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["factory", "coils", "coils2", "ship"].map((n) => <div key={n} className="img-frame aspect-square border border-[var(--stone)]"><img src={`/img/${n}.jpg`} alt="Facility" loading="lazy" className="w-full h-full object-cover" /></div>)}
        </div>
      </section>
    </div>
  );
}

export function Team() {
  const { data = [] } = useContent("team");
  useSeo("Leadership & Team", "The family and team behind Tijiya Exports, Kolkata.");
  return (
    <div data-testid="team-page">
      <PageHero eyebrow="Leadership & team" title="The people behind the plant" sub="A family-run organisation, with departments that have worked together for years." />
      <section className="wrap py-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {data.map((m, i) => (
          <Reveal key={m.id} delay={i * 100} className="card" data-testid={`team-card-${i}`}>
            <div className="aspect-[4/5] bg-[var(--stone)] flex items-center justify-center overflow-hidden">
              {m.image ? <img src={fileUrl(m.image)} alt={m.name} className="w-full h-full object-cover grey-hover" /> : <span className="serif text-6xl text-[var(--steel)]">{m.name.replace(/[[\]]/g, "").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
            </div>
            <div className="p-6">
              <h3 className="text-2xl">{m.name}</h3>
              <p className="eyebrow mt-1">{m.designation}</p>
              <p className="text-sm text-[var(--graphite)] mt-3">{m.bio}</p>
              {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="inline-block mt-4 text-[var(--steel)] hover:text-[var(--brass)]"><Linkedin size={18} /></a>}
            </div>
          </Reveal>
        ))}
      </section>
      <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-20">
        <div className="wrap">
          <SectionHead eyebrow="Departments" title="How we are organised" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[["Production", "Pressing, threading, forming and galvanising lines."], ["Quality Control", "Material, coating and dimensional inspection."], ["Exports & Logistics", "Documentation, packing and container loading."], ["Sales", "Quotations, samples and buyer support."]].map(([t, d]) => <div key={t} className="border-t border-[var(--stone)] pt-5"><h3 className="text-2xl">{t}</h3><p className="text-sm text-[var(--graphite)] mt-2">{d}</p></div>)}
          </div>
        </div>
      </section>
      <section className="wrap py-20"><div className="aspect-[21/7] bg-[var(--stone)] flex items-center justify-center text-sm text-[var(--graphite)]">[Team group photograph to be supplied]</div></section>
    </div>
  );
}

export function Achievements() {
  const { data: awards = [] } = useContent("awards");
  const { data: certs = [] } = useContent("certifications");
  const { data: s } = useSettings();
  const [img, setImg] = useState(null);
  useSeo("Achievements & Certifications", "Awards, certifications and recognition of Tijiya Exports, a Government of India recognised export house.");
  return (
    <div data-testid="achievements-page">
      <PageHero eyebrow="Achievements & certifications" title="Recognition, earned over decades" />
      <section className="wrap py-20">
        <SectionHead num="01" eyebrow="Awards" title="Awards" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {awards.map((a) => <div key={a.id} className="card p-7"><p className="serif text-3xl text-[var(--brass)]">{a.year}</p><h3 className="text-2xl mt-3">{a.name}</h3><p className="text-sm text-[var(--graphite)] mt-2">{a.body}</p></div>)}
        </div>
      </section>
      <section className="bg-[var(--paper)] border-y border-[var(--stone)] py-20">
        <div className="wrap">
          <SectionHead num="02" eyebrow="Certifications" title="Certificates & test reports" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {certs.map((c, i) => (
              <button key={c.id} data-testid={`cert-${i}`} onClick={() => c.image && setImg(fileUrl(c.image))} className="card text-left group">
                <div className="aspect-[4/3] bg-[var(--ivory)] flex items-center justify-center border-b border-[var(--stone)] img-frame">
                  {c.image ? <img src={fileUrl(c.image)} alt={c.name} className="max-h-full object-contain" /> : <span className="text-xs text-[var(--steel)]">[Certificate image]</span>}
                </div>
                <div className="p-6"><h3 className="text-xl">{c.name}</h3><p className="text-sm text-[var(--graphite)] mt-1">{c.issuer}</p></div>
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="wrap py-20"><Stats s={s} /></section>
      <Lightbox src={img} onClose={() => setImg(null)} />
    </div>
  );
}
